/* istanbul ignore file */
import React, { FunctionComponent, useEffect } from 'react';
import { useMeasure } from 'react-use';
import { Responsive } from '@noths/polaris-client-responsive';
import { Breakpoint, visuallyHidden } from '@noths/polaris-client-styles';
import { getPagePosition, getProductIdFromProductCode } from '@noths/polaris-client-utils';
import { Link } from '@noths/polaris-dev-ts-types';

import { BackToTopButton } from 'src/components/molecules/BackToTopButton';
import { LinkCarouselContainer, LinkCarouselType } from 'src/components/organisms/LinkCarousel';
import { ProductPageContainer } from 'src/components/organisms/ProductPage';
import { PRODUCTS_GRID_WRAPPER } from 'src/constants/elementIds';
import { UpdateHistoryPayload } from 'src/redux/products';
import { pageHistory } from 'src/services/pageHistory';
import { RELATED_CATEGORIES_TITLE } from '../constants/copy';
import * as styles from '../styles/ProductPages.styles';

export interface ProductPagesProps {
  pageNumbers: number[];
  relatedCategoryLinks: Link[];
}

type LastViewedProduct = Pick<UpdateHistoryPayload, 'productCode' | 'productOffset'>;

export const ProductPages: FunctionComponent<ProductPagesProps> = ({
  pageNumbers,
  relatedCategoryLinks,
}: ProductPagesProps) => {
  const [containerRef, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    const product = pageHistory!.get<LastViewedProduct>('lastViewedProduct');
    if (product) {
      const productId = getProductIdFromProductCode(product.productCode);
      const el = document.querySelector(`[data-productid="${productId}"]`);
      window.scrollTo(0, getPagePosition(el) - product.productOffset);
    }
  }, []);

  return (
    <div id={PRODUCTS_GRID_WRAPPER} css={styles.wrapper} ref={containerRef}>
      <h2 css={visuallyHidden}>Products</h2>
      {pageNumbers.map((page) => (
        <ProductPageContainer page={page} key={`product-page-${page}`} />
      ))}
      {relatedCategoryLinks.length > 0 && (
        <div css={styles.relatedCategories}>
          <LinkCarouselContainer
            id="related-categories"
            title={RELATED_CATEGORIES_TITLE}
            carouselType={LinkCarouselType.RelatedCategory}
            links={relatedCategoryLinks}
          />
        </div>
      )}
      <Responsive minBreakpoint={Breakpoint.XLARGE}>
        <BackToTopButton containerHeight={height} />
      </Responsive>
    </div>
  );
};
